<template>
  <div class="view activity">
    <div class="loading" :class="{show: loading}"></div>

    <h1>{{ model.name }}</h1>

    <div class="container">

      <!-- Activity Info -->
      <div class="meta">
        <div class="image-container">
          <img :src="model.imageUrl" alt="{Activity image caption}" class="image">
        </div>

        <ol class="icons">
          <li v-if="model.hasDuration"><span class="icon-duration"></span><duration :hours="model.durationHours" :minutes="model.durationMinutes"/></li>
          <li v-if="model.activityType !== 'rental' && cartItem.startTime"><span class="icon-time"></span>{{ $formatter.time(cartItem.startTime) }}</li>
          <li v-if="model.hasLunch"><span class="icon-lunch"></span>{{ $t('general.label.has-lunch') }}</li>
          <li v-if="model.hasPickup"><span class="icon-pickup"></span>{{ $t('general.label.has-pickup') }}</li>
          <li v-if="model.hasGiftCert"><span class="icon-gift"></span>{{ $t('general.label.has-giftcert') }}</li>
        </ol>

        <p class="description" v-html='model.description'></p>

        <div class="note" v-if="model.notes.length">
          <h2>{{ $t('general.label.notes-restrictions') }}</h2>
          <p v-html="model.notes"></p>
        </div>

        <div class="more-info">
          <input id="more-info-toggle" class="toggle-button" type="checkbox">
          <label for="more-info-toggle">{{ $t('general.label.more-info') }}</label>
          <p v-html='model.additionalBookingInformation'></p>
        </div>

        <div class="policy">
          <input id="policy-toggle" class="toggle-button" type="checkbox">
          <label for="policy-toggle">{{ $t('general.label.cancel-policy') }}</label>
          <p v-html='model.policies'></p>
        </div>
      </div>

      <!-- Form -->
      <div class="form" :class="{ active: bookNowActive }">

        <!-- Mobile fixed Book Now button -->
        <div class="mobile-fixed-button">
          <button v-on:click="bookNowToggle" class="mobile-book-now">
            <span class="from" v-if="showLowestPrice">{{ $t('general.label.price-from') }}</span>
            <span class="price" v-if="showLowestPrice">{{ $formatter.currency(model.listPrice) }}</span>
            {{ $t('general.label.book-now') }}
          </button>
        </div>

        <div class="inputs" :class="{ active: giftCertActive }">

          <!-- TODO We hiding this for now. -->
          <!-- If this gets used, make sure the selection is loaded when editing, since we have to manually make sure that all the input fields are loaded properly. -->
          <div class="gift-switch-container" style="display: none;">
            <span class="gift-switch-icon">{{ $t('general.label.gift') }}</span>
            <input type="checkbox" id="gift-switch" class="gift-switch" v-on:click="giftCertToggle">
            <label for="gift-switch"></label>
          </div>

          <!-- Gift Certificate inputs -->
          <div class="gift-inputs">
            <form-input type="text" :label="$t('form.label.date')" icon="date" v-model="cartItem.giftDate"/>
            <form-input type="text" :label="$t('form.label.gift.first-name')" icon="firstname" v-model="cartItem.giftFirstName"/>
            <form-input type="text" :label="$t('form.label.gift.last-name')" icon="lastname" v-model="cartItem.giftLastName"/>
            <form-input type="textarea" :label="$t('form.label.gift.message')" icon="giftmessage" v-model="cartItem.giftMessage"/>
          </div>

          <!-- Default inputs -->
          <div class="default-inputs">
            <!-- pickup label for multi-day rentals -->
            <div v-if="isMultiDayRental">
              <h3>{{ $t('general.label.pickup') }}</h3>
            </div>
            <availability-calendar :value="activeDate" @input="manuallyUpdateActiveDate" :enabled="model.availability" :unavailabilities="model.monthUnavailability" @changedMonth="changedMonth"/>

            <!-- Availability -->
            <div>
              <p class="availability" v-if="model.liveAvailability" v-html='availabilityMessage'></p>
              <p class="availability error" v-if="model.liveAvailability && !model.liveAvailability.bookable" v-html='availabilityError'></p>
            </div>

            <!-- Guest -->
            <guest-picker v-if="paxEnabled.length" ref="guestPicker" :paxPrefix="paxPrefix" :pricing="model.price" :enabled="paxEnabled" :value="defaultPax" @input="onGuestPickerUpdate"/>

            <form-input type="select" v-if="model.hasCustomRentalTimes" :label="$t('form.label.rental')" icon="time" :options="model.activeCustomRentalTimes" v-model="cartItem.customRentalTime" />

            <form-input type="select" v-if="startTimesActive" :label="startTimeLabel" icon="time" class="highlighted-info" :options="startTimes" v-model="cartItem.startTime" :info="customRentalInfo" />

            <!-- dropoff date for multi-day rentals -->
            <div v-if="isMultiDayRental">
              <h3>{{ $t('general.label.return') }}</h3>
              <availability-calendar @input="updateEndDate" :enabled="model.availability" v-model="cartItem.rawRentalEndDate" :unavailabilities="model.monthUnavailability"/>
            </div>
            <form-input type="select" v-if="endTimesActive" :label="$t('form.label.dropoff-time')" icon="time" :options="dropoffTimes" v-model="cartItem.endTime"/>


            <form-input type="select" v-if="model.pickups.length" :label="$t('form.label.pickup-location')" icon="pickup" class="highlighted-info" :info="pickupInfo" :options="pickupLocations" v-model="cartItem.pickupLocation"/>
            <p class="availability error" v-if="!pickupSelectionOk">{{ $t('form.label.pickup-required') }}</p>


            <form-input type="text" v-if="curPickup && curPickup.requireSpecificAddress" :label="$t('form.label.pickup-address')" icon="pickupaddress" v-model="cartItem.pickupAddress" :required="true"/>


            <form-input type="select" v-if="model.dropoffs.length" :label="$t('form.label.dropoff-location')" icon="dropoff" class="highlighted-info" :info="dropoffInfo" :options="dropoffLocations" v-model="cartItem.dropoffLocation"/>

            <form-input type="text" v-if="curDropoff && curDropoff.requireSpecificAddress" :label="$t('form.label.dropoff-address')" icon="dropoffaddress" v-model="cartItem.dropoffAddress" :required="true"/>

            <option-choices v-if="optionsAvailable" @change="updateOptions" :active="!loading" :cartItem="cartItem" />

            <meal-choices v-if="model.hasLunch" :cartItem="cartItem" :guestPicker="$refs.guestPicker" :initialMeals="editingCartItem?editingCartItem.meals:null" />
          </div>

          <div class="activity-add-to-cart">
            <div class="total-price">{{ $t('general.label.total') }}:<span>{{ $formatter.currency(cartItem.subTotal) }}</span></div>
            <button class="button-book" :class="{ active: buttonActive }" @click="onAddToCart">{{ editing ? $t('form.label.update-cart') : $t('form.label.add-to-cart') }}</button>
          </div>
        </div>
      </div>

      <div class="upsell" v-if="model.relatedPackages.length">
        <h2>{{ $t('general.label.related-packages') }}</h2>
        <item-list :items="model.relatedPackages" />
      </div>
    </div>
    <linked-products
      v-if="showLinkedProducts && model.relatedProducts && model.relatedProducts.length"
      ref="linkedProducts"
      :nextRoute="routeAfterProducts"
      :items="model.relatedProducts"
      >
    </linked-products>
    <linked-activities v-if="showLinkedActivities && model.relatedActivities && model.relatedActivities.length" ref="linkedActivities" :items="model.relatedActivities"></linked-activities>
  </div>
</template>

<script>
import moment from 'moment';
import AvailabilityCalendar from '@/components/AvailabilityCalendar.vue';
import GuestPicker from '@/components/GuestPicker.vue';
import FormInput from '@/components/FormInput.vue';
import ItemList from '@/components/ItemList';
import Duration from '@/components/Duration';
import LinkedProducts from '@/components/LinkedProducts.vue';
import LinkedActivities from '@/components/LinkedActivities.vue';
import MealChoices from '@/components/MealChoices.vue';
import OptionChoices from '@/components/OptionChoices.vue';

import PortalSettings from '@/models/PortalSettings';
import Activity from '@/models/Activity';
import Category from '@/models/Category';
import Product from '@/models/Product';

import Cart from '@/models/Cart';
import CartItem from '@/models/CartItem';

import PAP from '@/mixins/pap';
import dateItem from '@/mixins/dateItem';
import activityItem from '@/mixins/activityItem';
import constants from '@/include/constants';


export default {
  mixins: [ PAP, dateItem, activityItem ],

  props: ['showLinkedProducts', 'showLinkedActivities'],

  data() {
    return {
      type: 'activity', // for mixins
      bookNowActive: false,
      giftCertActive: false,
      paxPrefix: '',
      prevPax: undefined,
      prevStartTime: undefined,
      prevEndTime: undefined,
      prevRentalEndDate: undefined,
      cartItem: new CartItem(),
      loading: false,
      userChangedDateOrPax: false
    }
  },

  watch: {
    // update the activity model when the pax changes.
    'cartItem.pax': function(val) {
      let paxChanged = false;

      // if prevPax has already been set, check if the pax have changed.
      if(this.prevPax !== undefined) {
        for(let key in val) {
          if(val[key] !== this.prevPax[key]) {
            paxChanged = true;
          }
        }

        for(let key in this.prevPax) {
          if(val[key] !== this.prevPax[key]) {
            paxChanged = true;
          }
        }
      }

      // if the pax have changed, refresh the model
      if(paxChanged) {
        this.userChangedDateOrPax = true;
        this.refreshModel();
      }

      // save the pax so we can compare the values the next time it changes
      this.prevPax = val;
    },

    // update the activity model when the start time changes (only for rentals).
    'cartItem.startTime': function(val) {
      if(this.model.activityType === 'rental' || this.model.activityType === 'interval') {
        let startTimeChanged = false;

        // if prevStartTime has already been set, check if the start time have changed.
        if(this.prevStartTime !== undefined && this.prevStartTime !== val) {
          startTimeChanged = true;
        }

        // if the start time has changed, refresh the model
        if(startTimeChanged) {
          this.refreshModel();
        }
      }

      // save the start time so we can compare the values the next time it changes
      this.prevStartTime = val;
    },

    // update the activity model when the end time changes (only for rentals).
    'cartItem.endTime': function(val) {
      if(this.model.activityType === 'rental') {
        let endTimeChanged = false;

        // if prevEndTime has already been set, check if the end time have changed.
        if(this.prevEndTime !== undefined && this.prevEndTime !== val) {
          endTimeChanged = true;
        }

        // if the end time has changed, refresh the model
        if(endTimeChanged) {
          this.refreshModel();
        }
      }

      // save the end time so we can compare the values the next time it changes
      this.prevEndTime = val;
    },

    'cartItem.rentalEndDate': function(val) {
      if(this.model.activityType === 'rental') {
        let rentalEndDateChanged = false;

        // if prevRentalEndDate has already been set, check if the end time have changed.
        if(this.prevRentalEndDate !== undefined && this.prevRentalEndDate !== val) {
          rentalEndDateChanged = true;
        }

        // if the end time has changed, refresh the model
        if(rentalEndDateChanged) {
          this.refreshModel();
        }
      }

      // save the rental end date so we can compare the values the next time it changes
      this.prevRentalEndDate = val;
    },
  },

  computed: {
    model() {
      return Activity.find(this.id) || new Activity();
    },

    routeAfterProducts(){
      return { name: 'activity-linked-activities', params: this.$router.params, query: this.$router.query };
    },

    defaultPax() {
      return this.editing && this.editingCartItem ? this.editingCartItem.pax : this.guestPickerPax;
    },

    buttonActive() {
      if (this.loading){
        return false;
      }

      // check that we have availability
      let bookable = this.model.liveAvailability !== undefined && this.model.liveAvailability.bookable;

      // check that the start time and pickup location are valid
      bookable = bookable && this.startTimeSelectionOk && this.pickupSelectionOk;

      return bookable && !this.loading;
    },

    availabilityMessage() {
      let message = "";
      let remaining = this.model.liveAvailability.remaining;
      let inventoryStatus = this.model.liveAvailability.inventoryStatus;

      if(remaining !== undefined && remaining !== "" && inventoryStatus == constants.INVENTORY_STATUS_NEARING_CAPACITY) {
        message = this.$t("availability.spots-remaining", {0: remaining});
      }

      return message;
    },

    availabilityError() {
      let message = "";

      // if the activity is not bookable, build the localized error message
      if(this.model.liveAvailability.bookable == false) {
        let error = this.model.liveAvailability.error;

        for(let key in error) {
          let translationKey = error[key]["translation-key"];
          let translationFields = error[key]["translation-fields"];

          if(translationKey == 'LANG_MIN_TIME') {
            // format the minimum time
            let minTimeArray = translationFields[0];
            let days  = minTimeArray['d'] > 0 ? this.$tc("general.label.day-lowercase", minTimeArray['d'], { count: minTimeArray['d'] }) + ", " : "";
            let hours = minTimeArray['h'] > 0 ? this.$tc("general.label.hour-lowercase", minTimeArray['h'], { count: minTimeArray['h'] }) + ", " : "";
            let mins  = minTimeArray['m'] > 0 ? this.$tc("general.label.minute-lowercase", minTimeArray['m'], { count: minTimeArray['m'] }) + ", " : "";
            let secs  = minTimeArray['s'] > 0 ? this.$tc("general.label.second-lowercase", minTimeArray['s'], { count: minTimeArray['s'] }) + ", " : "";
            translationFields[0] = (days + hours + mins + secs).slice(0, -2); // remove the last comma
          }

          // build the translation
          message += this.$t("availability.error." + translationKey, translationFields);
        }
      }


      return message;
    },

    startTimesActive() {
      return this.model.activityType === 'interval' || this.model.activityType === 'rental' || this.model.activityType === 'thirdparty';
    },

    endTimesActive() {
      return this.model.activityType === 'rental' && !this.model.hasCustomRentalTimes;
    },

    isMultiDayRental() {
      return this.model.activityType === 'rental' && this.model.rentalIsMultiDay;
    },

    selectedCustomRentalTime() {
      let found = this.model.activeCustomRentalTimes.filter(item => item.value === this.cartItem.customRentalTime);
      return found && found.length ? found[0] : {};
    },

    customRentalInfo() {
      let info = '';
      let minutes = this.selectedCustomRentalTime.duration;

      if (this.model.hasCustomRentalTimes) {
        let duration = this.$formatter.duration(minutes);
        let et = moment(this.cartItem.startTime, 'HH:mm:ss').add(minutes, 'minutes').format('h:mm a');
        info = `${duration}, ` + this.$t("general.label.dropoff") + `: ${et}`;
      }

      return info;
    },

    // used for interval and rental activities
    intervalTimes() {
      let times = [];

      if (this.model.activeAvailability.startTime) {
        let st = this.model.activeAvailability.startTime;
        let et = this.model.activeAvailability.endTime;

        if (this.model.interval && this.model.interval != "00:00:00") {
          let activeDate = moment(this.activeDate).format('YYYY-MM-DD');

          let cur = moment(st, 'HH:mm:ss');
          let end = moment(et, 'HH:mm:ss');
          let interval = moment.duration(this.model.interval);
          while (cur.isSameOrBefore(end)) {
            times.push({
              value: cur.format('HH:mm:ss'),
              label: cur.format('h:mm a')
            });

            cur.add(interval);
          }
        }
      }
      return times;
    },

    // interval times that aren't in the past
    validStartTimes() {
      return this.intervalTimes.filter(time => {
        let intervalDateTime = moment(moment(this.activeDate).format('YYYY-MM-DD') + " " + time.value);

        return intervalDateTime.isSameOrAfter(moment());
      });
    },

    startTimes() {
      let times = [];
      if (this.model.activityType === 'interval') {
        // if there are times available
        if(this.model.liveAvailability !== undefined && this.model.liveAvailability.bookable) {
          for(let i in this.model.liveAvailability.timeslots) {
            let time = moment(this.model.liveAvailability.timeslots[i].time, 'HH:mm');
            let label = time.format('h:mm a');

            let remaining = this.model.liveAvailability.timeslots[i].remaining;
            let bookable = this.model.liveAvailability.timeslots[i].bookable;
            let inventoryStatus = this.model.liveAvailability.timeslots[i].inventoryStatus;
            let disabled = bookable ? undefined : "disabled";

            if(remaining && inventoryStatus == constants.INVENTORY_STATUS_NEARING_CAPACITY) {
              label += " (" + remaining + " " + this.$t("general.label.remaining-lowercase") + ")";
            } else if(!bookable) {
              label += " (" + this.$t("general.label.sold-out") + ")";
            }

            times.push({
              value: time.format('HH:mm:ss'),
              label: label,
              disabled: disabled,
              bookable: bookable
            });
          }
        } else {
          // show no times
          let label = this.$t('availability.error.no-times');
          times.push({
            selected: "selected",
            value: "",
            label: label,
          });
        }
      } else if (this.model.activityType === 'rental') {
        if (this.model.hasCustomRentalTimes) {
          if (this.selectedCustomRentalTime.times) {
            times = this.selectedCustomRentalTime.times.map(item => {
              return {
                value: `${item}:00`,
                label: moment(item, 'HH:mm').format('h:mm a')
              };
            });
          }
        } else if (this.isMultiDayRental) {
          // If it's multi-day rental and the start and end date are the same,
          // we don't let the customer pickup on the very last available start time.
          if(moment(this.cartItem.rentalEndDate).isSame(moment(this.activeDate), 'day')) {
            times = this.validStartTimes.slice(0, -1);
          } else {
            // If the end date is different, anything goes.
            times = this.validStartTimes;
          }
        } else {
          // If this is a rental activity, we don't let the customer pickup on the very last available time
          times = this.validStartTimes.slice(0, -1);
        }
      } else if(this.model.activityType === 'thirdparty') {
        times = this.model.departureTimes;
      }

      return times;
    },

    // we want to filter out any times on or before the current startTime
    dropoffTimes() {
      let curStartTime = moment(this.cartItem.startTime, 'HH:mm:ss');
      let dropoffTimes = this.intervalTimes;

      // if this is not a mult-day rental or it is and the start and end date are the same, remove any times that are before the start time
      if(!this.isMultiDayRental || moment(this.cartItem.rentalEndDate).isSame(moment(this.activeDate), 'day')) {
        dropoffTimes = dropoffTimes.filter(item => {
          return moment(item.value, 'HH:mm:ss').isAfter(curStartTime);
        });
      }

      return dropoffTimes;
    },

    startTimeLabel() {
      let key = 'form.label.start-time';
      if (this.model.activityType == 'rental') {
        key = 'form.label.pickup-time';
      }
      return this.$t(key);
    },

    startTimeSelectionOk() {
      let startTimeBookable = true;

      if(this.model.activityType === 'interval') {
        startTimeBookable = false;

        // get the index of the selected start time to see if it's bookable
        let i = this.startTimes.findIndex(startTime => startTime.value === this.cartItem.startTime);
        startTimeBookable = (i >= 0) ? this.startTimes[i].bookable : false;
      }

      return startTimeBookable;
    },

    requiredFrontEndPickup() {
      return PortalSettings.get('requiredFrontEndPickup');
    },

    pickupSelectionOk() {
      return !this.requiredFrontEndPickup || !this.model.pickups.length || this.cartItem.pickupLocation > 0;
    },

    pickupLocations() {
      return this.model.pickups.map(this.mapLocation);
    },

    dropoffLocations() {
      return this.model.dropoffs.map(this.mapLocation);
    },

    curPickup() {
      let cur = this.model.pickups.filter(item => item.id === this.cartItem.pickupLocation);
      return cur.length ? cur[0] : null;
    },

    curDropoff() {
      let cur = this.model.dropoffs.filter(item => item.id === this.cartItem.dropoffLocation);
      return cur.length ? cur[0] : null;
    },

    pickupInfo() {
      return this.locationInfo('pickup', this.curPickup);
    },

    dropoffInfo() {
      return this.locationInfo('dropoff', this.curDropoff);
    },

    showLowestPrice() {
      return PortalSettings.get('showLowestPrice', true);
    },

    optionsAvailable() {
      return this.cartItem.details && this.cartItem.details.options.length;
    }
  },

  methods: {
    manuallyUpdateActiveDate(val, hard, doRefresh) {
      this.userChangedDateOrPax = true;
      this.updateActiveDate(val, hard, doRefresh);
    },

    changeToNextAvailableDate() {
      let bookable = this.model.liveAvailability !== undefined && this.model.liveAvailability.bookable;

      // disable the 'calendar walk' availability search:
      // - once the user starts manually selecting dates or pax, or
      // - if we are editing an activity already in the cart
      if (!this.userChangedDateOrPax && !this.editing && this.model.liveAvailability !== undefined && !bookable){
        let nextAvailableDates = this.model.liveAvailability.nextAvailable !== undefined ? Object.keys(this.model.liveAvailability.nextAvailable.dates).sort() : [];
        if (nextAvailableDates.length > 0){
          if(this.isMultiDayRental && this.cartItem.rentalEndDate < nextAvailableDates[0]){
            this.cartItem.rawRentalEndDate = moment(nextAvailableDates[0]).toDate();
            this.cartItem.rentalEndDate = nextAvailableDates[0];
          }
          this.updateActiveDate(moment(nextAvailableDates[0]).toDate(),true);
        }
      }
    },

    changedMonth(date){
      if (date.getMonth() != this.activeDate.getMonth()) {
        this.refreshModel(false, date);
      } else {
        this.refreshModel(false);
      }
    },
    // implemented for dateItem mixin
    refreshModel(hard = false, tempMonth = null) {
      // disable the "add to cart" button while we refresh the model
      this.loading = true;

      let activeCartItem = this.cartItem;

      if (hard && this.editing && this.editingCartItem) {
        activeCartItem = this.editingCartItem;
        this.updateActiveDate(moment(activeCartItem.date).toDate(), false, false);
      }
      this.cartItem.date = moment(this.activeDate).format('YYYY-MM-DD');
      if(tempMonth){
        activeCartItem.tempMonth = tempMonth;
      }

      return Activity.fetch(this.id, moment(this.activeDate).format('YYYY-MM-DD'), activeCartItem).then(() => {
        this.resetItem(hard, activeCartItem);
        this.changeToNextAvailableDate();

        if (hard) {
          Category.fetch(this.type, this.model.category);
        }
        // re-enable the "add to cart" button
        this.loading = false;
      });
    },

    resetItem(hard = false, activeCartItem = null) {
      if (!activeCartItem) {
        activeCartItem = this.cartItem;
      }

      let options = activeCartItem.options;
      let startTime = activeCartItem.startTime;
      let endTime = activeCartItem.endTime;
      let rentalEndDate = activeCartItem.rentalEndDate;
      let pax = activeCartItem.pax;


      if (this.model.options) {
        options = this.model.options.map(option => {
          let sel = activeCartItem.options ? activeCartItem.options.find(o => o.id === option.categoryId) : null;
          return {
            id: option.categoryId,
            value: sel ? sel.value : option.value
          }
        });
      }


      if(hard && !this.editing) {

        startTime = this.model.listStartTime;
        // set the start time to the first bookable time
        for(let i in this.startTimes) {
          if(this.startTimes[i].disabled === undefined) {
            startTime = this.startTimes[i].value;

            let cur = moment(startTime, 'HH:mm:ss');
            cur.add(moment.duration(this.model.interval));
            endTime = cur.format('HH:mm:ss');
            break;
          }
        }

        if(this.model.activityType === 'rental') {
          rentalEndDate = moment(this.activeDate).format('YYYY-MM-DD');
        }

      }

      if (hard) {
        // model has updated so set the prefix so guest-picker uses correct lang overrides.
        pax = this.defaultPax;
        this.paxPrefix = `activity${this.id}.`;
        this.$refs.guestPicker.reset();
      }

      let defaults = {
        date: activeCartItem.date,
        id: activeCartItem.id,
        itemId: this.id,
        type: 'activity',
        cartId: activeCartItem.cartId,
        startTime: startTime,
        endTime: endTime,
        pax: pax == undefined ? this.defaultPax : pax,
        meals: this.cartItem.meals, // a case where we don't take it from active in case we're editing. so the defaults are set some other way
        options: options,
        pickupLocation: activeCartItem.pickupLocation || (this.pickupLocations.length ? this.pickupLocations[0].value : undefined),
        dropoffLocation: activeCartItem.dropoffLocation || (this.dropoffLocations.length ? this.dropoffLocations[0].value : undefined),
        pickupAddress: activeCartItem.pickupAddress,
        dropoffAddress: activeCartItem.dropoffAddress,
        customRentalTime: activeCartItem.customRentalTime,
        rentalEndDate: rentalEndDate,
        rawRentalEndDate: moment(rentalEndDate).toDate(), // set the input for the end date calendar in the correct format
      };

      // do this last
      this.cartItem = new CartItem(defaults);

    },

    updateOptions(option) {
      if (option.addToInventory) {
        this.refreshModel();
      }
    },

    updateEndDate(endDate) {
      this.userChangedDateOrPax = true;
      this.cartItem.rentalEndDate = moment(endDate).format('YYYY-MM-DD');
    },

    locationInfo(type, loc) {
      let info = '';
      if (loc) {
        // TODO jamie i18n
        info = `Estimated ${type} time: ${this.$formatter.time(loc.startTime)}`;

        if (loc.endTime != loc.startTime) {
          info += ` - ${this.$formatter.time(loc.endTime)}`;
        }
      }
      return info;
    },

    onAddToCart() {
      // console.log('add to cart', this.cartItem);
      this.cartItem.date = moment(this.activeDate).format('YYYY-MM-DD');
      this.loading = true;

      // set the rental start/end times
      if(this.model.activityType === 'rental') {
        this.cartItem.rentalStartTime = this.cartItem.startTime;
        this.cartItem.rentalEndTime = this.cartItem.endTime;
      }

      this.cartItem.image = this.model.image;

      Cart[this.editing ? 'update' : 'add'](this.cartItem).then(() => {
        this.resetItem();
        // redirect to the cart TODO jamie handle errors
        if (!this.editing && this.model.relatedProducts.length) {
          this.$router.push({name: 'activity-linked-products', params: { id: this.cartItem.itemId,  date: this.cartItem.date  }});
          this.updateActiveDate(this.activeDate);
        } else if (!this.editing && this.model.relatedActivities.length) {
          this.$router.push({name: 'activity-linked-activities', params: { id: this.cartItem.itemId,  date: this.cartItem.date  }});
          this.updateActiveDate(this.activeDate);
        } else {
          this.$router.push({name: 'cart'});
        }
        this.loading = false;
      });
    },

    bookNowToggle() {
      // TODO Jamie - Add overflow hidden to #app(or body) when this is active
      this.bookNowActive = !this.bookNowActive;
    },

    giftCertToggle() {
      this.giftCertActive = !this.giftCertActive;
    },

    mapLocation(item) {
      return {
        label: item.name,
        value: item.id
      };
    }
  },

  components: {
    FormInput,
    AvailabilityCalendar,
    GuestPicker,
    ItemList,
    Duration,
    LinkedProducts,
    LinkedActivities,
    MealChoices,
    OptionChoices
  }
}
</script>

<style lang="scss">
.view.activity {
  @include breakpoint(medium) {
    .container {
      display: flex;
      flex-wrap: wrap;
    }
  }

  // Activity info area
  .meta {
    width: 100%;
    margin-bottom: 50px;
    @include breakpoint(medium) {
      width: calc(100% - 330px);
      margin-right: 30px;
    }

    .image-container {
      height: 0;
      overflow: hidden;
      padding-top: 56.25%;
      background: #fff;
      position: relative;
    }

    .image {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }

    .icons {
      list-style: none;
      margin: 10px 0 0 0;
      padding: 0;

      li {
        padding: 3px 0;

        >span {
          margin-right: 10px;
          font-size: 20px;
          text-align: center;
          width: 24px;
          color: $colorGray;
        }

        .icon-duration { @include font-awesome($fa-var-hourglass-half, light); }
        .icon-time { @include font-awesome($fa-var-watch, light); }
        .icon-lunch { @include font-awesome($fa-var-utensils, light); }
        .icon-pickup { @include font-awesome($fa-var-bus, light); }
        .icon-gift { @include font-awesome($fa-var-gift-card, light); }
      }
    }

    .description {
      margin: 15px 0 30px 0;
    }

    .note {
      border-top: 1px solid $colorPaleGray;
      padding: 24px 0 18px 0;
    }

    .note p {
      padding-top: 6px;
      padding-bottom: 6px;
    }

    .more-info {
      @include toggle-info;
      border-bottom: none;
    }

    .policy {
      @include toggle-info;
    }
  }

  .mealcomment .icon { @include font-awesome($fa-var-comment-dots, light); }


  // Form area
  .form {
    background-color: transparent;
    position: fixed;
    width: 100%;
    margin-top: 400px;
    bottom: 0;
    right: 0;
    z-index: 1;
    transition: background-color .1s ease .4s;

    @include breakpoint(medium) {
      width: 300px;
      position: relative;
      margin-top: 0;
      margin-bottom: 50px;
      z-index: unset;

      .inputs {
        max-height: none !important;
        background-color: $colorBgGray;
        padding: 25px 25px 15px 25px;
      }
    }

    // When the mobile form is active
    &.active {
      background-color: #fff;
      height: 100%;
      top: 0;
      margin-top: 0;
      overflow: scroll;
      z-index: 2;
      transition: margin-top .4s;

      .mobile-fixed-button {
        margin-bottom: 20px;

        .price {
          display: none;
        }

        .mobile-book-now {
          visibility: hidden;

          &::before {
            @include font-awesome-pseudo($fa-var-times, light);
            color: $colorGray;
            visibility: visible;
            position: absolute;
            top: 15px;
            right: 15px;
            cursor: pointer;
            font-size: 30px;
          }
        }
      }

      .inputs {
        max-height: 500px;
        overflow: unset;
        transition: max-height .4s;
      }
    }
  }

  .mobile-fixed-button {
    display: flex;
    justify-content: flex-end;
    padding-right: 15px;
    margin-bottom: 45px;

    @include breakpoint(medium) {
      display: none;
    }

    .from {
      @include font-smallest;
      color: #fff;
      opacity: .8;
      margin-right: 3px;
    }

    .price {
      opacity: .8;
      margin-right: 3px;

      &::after {
        content: '|';
        margin-left: 5px;
        opacity: .2;
      }
    }

    .mobile-book-now {
      @include button($colorCTA, #fff);
      border-radius: 30px;
      box-shadow: 1px 3px 3px rgba(0,0,0,.2);
    }
  }

  .desktop-price {
    display: none;
    @include breakpoint(medium) {
      display: block;
      background-color: $colorBgDarkGray;
      color: #fff;
      padding: 15px 25px;

      .price {
        @include font-h2;
        margin-left: 5px;
      }
    }
  }

  // Gift Certificate switch
  .gift-switch-container {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 20px;
  }

  .gift-switch-icon {
    @include font-smaller;
    color: $colorDarkGray;
    margin-right: 4px;

    &::before {
      @include font-awesome-pseudo($fa-var-gift, light);
      font-size: 20px;
      margin-right: 4px;
    }
  }

  .gift-switch {
    @include switch;
  }

  .inputs {
    .gift-inputs {
      display: none;
    }

    &.active {
      .gift-inputs {
        display: block;
      }
      .default-inputs {
        display: none;
      }
    }
  }

  .inputs {
    padding: 0 15px;
    max-height: 0;
    transition: all .6s;

    .date .icon { @include font-awesome($fa-var-calendar-alt, light); }
    .firstname .icon { @include font-awesome($fa-var-smile, light); }
    .lastname .icon { @include font-awesome($fa-var-smile, light); }
    .giftmessage .icon { @include font-awesome($fa-var-comment-dots, light); }
    .time .icon { @include font-awesome($fa-var-watch, light); }
    .pickup .icon { @include font-awesome($fa-var-map-marker-alt, light); }
    .pickupaddress .icon { @include font-awesome($fa-var-map-marker-edit, light); }
    .dropoff .icon { @include font-awesome($fa-var-map-marker-alt, light); }
    .dropoffaddress .icon { @include font-awesome($fa-var-map-marker-edit, light); }
    .option .icon { @include font-awesome($fa-var-plus, light); }
    .meal .icon { @include font-awesome($fa-var-utensils, light); }
    // .mealcomment .icon { @include font-awesome($fa-var-comment-dots, light); }
  }


  // Option
  .option-container {
    display: flex;
    margin-bottom: 20px;

    > div {
      width: 100%;
    }
  }

  .option-icon {
    @include font-awesome($fa-var-plus, light);
    color: $colorDarkGray;
    font-size: 20px;
    line-height: 50px !important;
    width: 25px;
    margin-right: 15px;
    text-align: center;

    @include breakpoint(medium) {
      display: none;
    }
  }

  .option-input {
    display: grid;
    display: -ms-grid;
    grid-template-columns: 1fr 100px;
    -ms-grid-columns:  1fr 100px;
    grid-gap: 10px;
    align-items: center;
    width: 100%;

    /*  IE */
    @media all and (-ms-high-contrast:none) {
      .quantity-picker {
        -ms-grid-column: 2;
      }
    }

    input {
      background-color: transparent;
    }
  }

  .option-name {
    @include font-input;
  }

  .option-price {
    color: $colorGray;
  }

  .option-description {
    @include font-small;
    color: $colorGray;
    margin-top: 3px;
  }


  .activity-add-to-cart {
    margin-bottom: 15px;
  }

  .total-price {
    @include font-h2;
    @include form-border-top;
    text-align: right;
    margin-bottom: 20px;

    span {
      margin-left: 10px;
    }
  }

  .button-book {
    /*@include button($colorCTA, #fff);
    width: 100%;
    margin-bottom: 70px;

    @include breakpoint(medium) {
      margin-bottom: 20px;
    }*/
    @include inactive-button($colorCTA, #fff);
  }

  .availability {
    text-align: left;
    margin-bottom: 32px;

    span {
      margin-left: 10px;
    }
  }

  .textarea{
    textarea{
      padding-left: 40px;
    }
  }

  .error {
    color: $colorRed;
    margin-top: -14px;
  }

  .meal-title {
    @include form-border-top;
    margin-bottom: 15px;
    margin-top: 30px;
    line-height: 40px;
  }

  .meal-details {
    margin-bottom: 20px;
  }

  // Upsell area
  .upsell {
    @include full-width;
    background-color: $colorBgGray;
    padding-top: 20px;
    padding-bottom: 20px;
    margin-bottom: 20px;
    @include breakpoint(medium) {
      background-color: #fff;
    }

    h2 {
      margin-bottom: 15px;
    }
  }
}
</style>